import { useState, useRef, FC } from 'react';
import classNames from 'classnames';
import { useClickOutside } from 'react-easyrocket';
import { DropdownItem } from './components/DropdownItem';
import { Options } from 'src/components/CustomCalendar/types';
import { Arrow } from 'src/components/svg/Arrows';

interface DropdownCalendarProps {
  options: Options[];
  selected: Options;
  onSelect: (value: Options) => void;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
  dropdownTextOrientation?: 'center' | 'left';
  tabIndex?: 0 | -1;
}

export const DropdownCalendar: FC<DropdownCalendarProps> = ({
  options,
  selected,
  onSelect,
  disabled,
  className,
  tabIndex,
  dropdownTextOrientation = 'left',
  placeholder = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useClickOutside(dropdownRef, () => setIsOpen(false));

  const handleToggleDropdown = () => {
    setIsOpen((prev) => !prev);
  };

  const handleSelectItem = (option: Options) => {
    onSelect(option);
    setIsOpen(true);
  };

  return (
    <div
      role="button"
      tabIndex={tabIndex}
      onClick={handleToggleDropdown}
      ref={dropdownRef}
      className={classNames(className, 'relative max-w-24 rounded-[20px] shrink-[0]')}>
      <button
        type="button"
        disabled={disabled}
        style={{ transform: 'translate3d(0, 0, 0)' }}
        className={classNames(
          'custom-calendar disabled:text-i_text_disabled disabled:bg-i_bg_disabled disabled:cursor-not-allowed capitalize cursor-pointer outline-none relative z-[3] h-10 bg-i_bg py-2.5 w-full pl-4 pr-3 flex justify-between items-center rounded-[20px] border border-solid border-i_border',
          placeholder && !selected && '!text-input-border',
          selected ? 'text-base text-i_text' : 'text-i_placeholder text-base'
        )}>
        {selected || placeholder}
      </button>
      <Arrow
        className={classNames(
          '!w-3 h-10 !stroke-[3px] absolute top-0 z-[3] right-3 transition-transform will-change-transform',
          isOpen && 'opened',
          disabled ? 'pointer-events-none stroke-i_text_disabled' : 'stroke-i_icon'
        )}
      />
      {isOpen && (
        <ul
          className={classNames(
            isOpen ? 'max-h-[120px] ' : 'h-0',
            'rounded-b-[20px] bg-i_dropdown_bg border border-solid border-i_dropdown_border flex flex-col items-start overflow-y-auto w-full pt-4 pb-2 shadow absolute top-5 z-[2]'
          )}>
          {options.map((option, index) => {
            return (
              <DropdownItem
                key={index}
                item={option}
                dropdownTextOrientation={dropdownTextOrientation}
                onClick={handleSelectItem}
              />
            );
          })}
        </ul>
      )}
    </div>
  );
};
